import { calcAPCA } from "apca-w3";
import randomColor from "randomcolor";

import { normalize } from "~/utils/normalize";

export function isImage(originalUrl: string | URL) {
  const url = normalize(originalUrl);
  return (
    /\.(?:gif|jpe?g|png|webp|avif)$/.test(url.pathname) ||
    (url.protocol === "data:" && url.pathname.startsWith("image/"))
  );
}

export function isLocalImage(originalUrl: string | URL) {
  const url = normalize(originalUrl);
  return (
    isImage(url) &&
    url.host === import.meta.env.VITE_API_ENDPOINT &&
    url.pathname.startsWith("/files/")
  );
}

export function extractEmbed(originalUrl: string | URL) {
  const url = normalize(originalUrl);

  if (url.protocol === "data:") {
    return "about:blank";
  }

  if (/(?:\.|^)youtube(?:-nocookie)?\.com$/.test(url.host)) {
    return `https://youtu.be/${url.pathname.replace(/^\/embed\//, "")}`;
  }

  return url.href;
}

export function resolveColorScheme() {
  const colorScheme = localStorage.getItem("colorScheme") ?? "system";
  if (colorScheme !== "system") {
    return colorScheme;
  }

  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function generateColor(seed: string, alpha = 1) {
  const color = randomColor({
    luminosity: resolveColorScheme() === "dark" ? "bright" : "dark",
    seed,
    alpha,
    format: "rgba",
  });

  const htmlStyles = getComputedStyle(document.documentElement);
  const lightTextColor = htmlStyles.getPropertyValue("--color-zinc-950");
  const darkTextColor = htmlStyles.getPropertyValue("--color-zinc-50");

  const lightContrast = Math.abs(calcAPCA(lightTextColor, color) as number);
  const darkContrast = Math.abs(calcAPCA(darkTextColor, color) as number);
  const textColor =
    lightContrast > darkContrast ? lightTextColor : darkTextColor;

  return { color, textColor };
}

export async function uploadFile(
  file: File,
  withPostId?: boolean,
  onProgress?: (progress: number) => void,
) {
  const body = new FormData();
  body.append("file", file);

  if (withPostId) {
    const postId = location.href.replaceAll(
      /^https?:\/\/[^/]+\/edit-post\//gi,
      "",
    );
    body.append("postId", postId);
  }

  const xhr = new XMLHttpRequest();
  return new Promise<any>((resolve, reject) => {
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded / event.total);
        }
      });
    }

    xhr.addEventListener("loadend", () => {
      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
        resolve(xhr.response);
        return;
      }

      reject(new Error(`${xhr.status.toString()} ${xhr.statusText}`));
    });

    xhr.open("POST", `${import.meta.env.VITE_API_ENDPOINT}/upload`, true);
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.send(body);
  });
}
